import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Form, Input, Button, FormProps, Typography, Radio, Select, DatePicker, Cascader, Upload, Row, Col, Divider, message } from 'antd';
const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
import axios from 'axios';
import { SendOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { provinceData } from './common/ProvinceData';

type FieldType = {
  position?: string;
  name?: string;
  gender?: string;
  phone?: string;
  email?: string;
  birthDate?: string;
  province?: string[];
  address?: string;
  education?: {
    degree?: string;
    graduateDate?: string;
    school?: string;
    major?: string;
  };
  trainingExperience?: string;
  workExperience?: string;
  selfEvaluation?: string;
  expectedSalary?: string;
  attachments?: UploadFile[];
};

const Require: React.FC = () => {
  const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

  const { t } = useTranslation();
  const [form] = Form.useForm();

  const provinceOptions = provinceData;

  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    try {
      const emailBody = `
        应聘岗位: ${values.position}\n
        姓名: ${values.name}\n
        性别: ${values.gender}\n
        电话: ${values.phone}\n
        邮箱: ${values.email}\n
        出生日期: ${values.birthDate}\n
        所在地区: ${values.province?.join('/')}\n
        详细地址: ${values.address}\n
        学历: ${values.education?.degree}\n
        毕业时间: ${values.education?.graduateDate}\n
        毕业院校: ${values.education?.school}\n
        专业: ${values.education?.major}\n
        培训经历: ${values.trainingExperience}\n
        工作经历: ${values.workExperience}\n
        自我评价: ${values.selfEvaluation}\n
        期望薪资: ${values.expectedSalary}\n      `;

        await axios.post(`${API_BASE}/inquiry/send-email/`, {
        to: 'sales@eetl-evo.com',
        subject: '新的职位申请',
        body: emailBody
      });

      message.success(t('require.sendSuccess'));
      form.resetFields();
    } catch (error) {
      console.error('提交失败:', error);
      message.error(t('require.sendFailed'));
    }
  };

  return (
    <div className='container pt-40 pb-60'>
      <div className='mx-auto p-20 pt-40' style={{backgroundColor: '#f8f8f8',maxWidth: '1200px'}}>
        <Title level={2} className="text-center mb-4">{t('require.title')}</Title>
        <Card  className="page-card" style={{border: 'none'}}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Title className='pt-10' level={5}>{t('require.position_section')}</Title>
            <Row gutter={32}>
              <Col span={24}>
                <Form.Item
                  label=""
                  name="position"
                  rules={[{ required: true, message: t('require.validation.position') }]}
                >
                  <Radio.Group
                    style={{ display: 'flex', gap: 8 }}
                  >
                    <Radio value={t('require.position_options.sales')}>{t('require.position_options.sales')}</Radio>
                    <Radio value={t('require.position_options.customer_service')}>{t('require.position_options.customer_service')}</Radio>
                    <Radio value={t('require.position_options.product_manager')}>{t('require.position_options.product_manager')}</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            <Title className='mb-20 pt-10' level={5}>{t('require.selfinfo')}</Title>
            <Row gutter={32}>
              <Col span={12}>
                <Form.Item
                  label={t('require.personal_info.name')}
                  name="name"
                  rules={[{ required: true, message: t('require.validation.name') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('require.personal_info.gender')}
                  name="gender"
                  rules={[{ required: true, message: t('require.validation.gender') }]}
                >
                  <Radio.Group>
                    <Radio value="male">{t('require.gender.male')}</Radio>
                    <Radio value="female">{t('require.gender.female')}</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('require.personal_info.phone')}
                  name="phone"
                  rules={[
                    { required: true, message: t('require.validation.phone') },
                    { pattern: /^1[3-9]\d{9}$/, message: t('require.validation.phoneType') }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('require.personal_info.email')}
                  name="email"
                  rules={[
                    { required: true, message: t('require.validation.email') },
                    { type: 'email', message: t('require.validation.emailType') }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('require.personal_info.birth_date')}
                  name="birthDate"
                  rules={[{ required: true, message: t('require.validation.birthDate') }]}
                >
                  <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('require.personal_info.province')}
                  name="province"
                  rules={[{ required: true, message: t('require.validation.province') }]}
                >
                  <Cascader options={provinceOptions} placeholder={t('require.validation.province')} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label={t('require.personal_info.detail_address')}
              name="address"
              rules={[{ required: true, message: t('require.validation.detailAddress') }]}
            >
              <Input />
            </Form.Item>

            <Title className='mb-20 pt-10' level={5}>{t('require.education_section')}</Title>
            <Row gutter={32}>
              <Col span={12}>
                <Form.Item
                  label={t('require.education.degree')}
                  name={['education', 'degree']}
                  rules={[{ required: true, message: t('require.validation.education') }]}
                >
                  <Select placeholder={t('require.validation.education')}>
                    <Option value="专科">{t('require.education.options.other')}</Option>
                    <Option value="本科">{t('require.education.options.undergraduate')}</Option>
                    <Option value="硕士">{t('require.education.options.master')}</Option>
                    <Option value="博士">{t('require.education.options.phd')}</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('require.education.graduate_date')}
                  name={['education', 'graduateDate']}
                  rules={[{ required: true, message: t('require.validation.graduateDate') }]}
                >
                  <DatePicker format="YYYY-MM" picker="month" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('require.education.school')}
                  name={['education', 'school']}
                  rules={[{ required: true, message: t('require.validation.school') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('require.education.major')}
                  name={['education', 'major']}
                  rules={[{ required: true, message: t('require.validation.major') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={24}>
              
                <Form.Item
                  label="培训经历"
                  name="trainingExperience"
                >
                  <TextArea rows={4} />
                </Form.Item>
              </Col>
            </Row>
            <Divider/>
            <Form.Item
              label={t('require.work_experience')}
              name="workExperience"
              rules={[{ required: true, message: t('require.validation.work_experience') }]}
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              label={t('require.self_evaluation')}
              name="selfEvaluation"
              rules={[{ required: true, message: t('require.validation.self_evaluation') }]}
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              label={t('require.expected_salary')}
              name="expectedSalary"
              rules={[{ required: true, message: t('require.validation.expected_salary') }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t('require.attachments.label')}
              name="attachments"
              extra={t('require.attachments.extra')}
            >
              <Upload
                action=""
                beforeUpload={(file) => {
                  if (file.size > 50 * 1024 * 1024) {
                    console.error('文件大小超过50MB');
                    return false;
                  }
                  return true;
                }}
              >
                <Button>{t('require.choosefile')}</Button>
              </Upload>
            </Form.Item>

            <Form.Item className='text-center pt-20 pb-20'>
              <Button className='pl-50 pr-50' type="primary" htmlType="submit"  icon={<SendOutlined />} shape="round" size='large'>
              {t('require.sendResume')}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default Require;